import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Clock, MapPin, Users, UsersThree, Download, PencilSimple, Trash, Eye, XCircle, ArrowCounterClockwise, CloudArrowUp, CloudSlash, FileText, Calendar, Queue, Copy, EnvelopeSimple, Camera } from '@phosphor-icons/react';
import { formatDatePST, formatTime } from './utils';

/**
 * SessionCard - Display a single session with action buttons
 */
export function SessionCard({
  session,
  onViewRoster,
  onViewWaitlist,
  onEdit,
  onDelete,
  onCancel,
  onRestore,
  onPublish,
  onUnpublish,
  onExportCSV,
  onCopy,
  onMessage,
  onViewPhotos,
  onEndSession,
  onReopenSession,
  cancellingSession
}) {
  const isCancelled = session.status === 'cancelled';
  const isEnded = session.status === 'ended';
  const isDraft = session.published === false;
  const isFull = session.registered_count >= session.capacity;

  return (
    <Card 
      className={`bg-card border-border card-hover ${isCancelled ? 'opacity-60' : ''} ${isEnded ? 'opacity-75 border-gray-500/50' : ''} ${isDraft ? 'border-yellow-500/50' : ''}`}
    >
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading text-lg">{formatDatePST(session.date)}</h3>
              {isDraft && (
                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-500 text-xs font-medium rounded-full flex items-center gap-1">
                  <FileText size={12} />
                  DRAFT
                </span>
              )}
              {isEnded && (
                <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs font-medium rounded-full flex items-center gap-1">
                  <XCircle size={12} />
                  ENDED
                </span>
              )}
              {isCancelled && (
                <span className="px-2 py-0.5 bg-red-500/20 text-red-500 text-xs font-medium rounded-full">
                  CANCELLED
                </span>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm font-medium">{session.registered_count} / {session.capacity}</p>
              <p className="text-xs text-muted-foreground">registered</p>
            </div>
          </div>
          
          {/* Session Title (if present) */}
          {session.title && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg px-3 py-2">
              <p className="text-sm font-bold text-primary">{session.title}</p>
            </div>
          )}
          
          {/* Grade Group Badge */}
          <div className="flex items-center gap-2 mb-2">
            <UsersThree size={16} weight="duotone" className="text-primary" />
            <span className="text-sm font-medium px-2 py-0.5 bg-primary/10 rounded-full text-primary">
              {session.grade_group === 'elementary' ? '3rd-5th Grade' : 
               session.grade_group === 'middle' ? '6th-8th Grade' : 
               session.grade_group === 'high' ? '9th-12th Grade' : 
               session.age_group || 'Elementary'}
            </span>
          </div>
          
          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock size={16} weight="duotone" />
                <span>{formatTime(session.start_time)} - {formatTime(session.end_time)}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} weight="duotone" />
                <span>{session.location?.name}</span>
              </div>
            </div>
            
            <div className="space-y-2">
              {session.court && (
                <div className="flex items-center gap-2">
                  <Calendar size={16} weight="duotone" />
                  <span>{session.court}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Users size={16} weight="duotone" />
                <span>{session.checked_in_count} checked in</span>
              </div>
            </div>
          </div>
          
          {/* Price */}
          {session.price > 0 && (
            <div className="pt-2 border-t border-border">
              <p className="text-sm font-medium text-primary">${session.price.toFixed(2)} per player</p>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onViewRoster(session)}
              data-testid={`view-roster-${session.session_id}`}
              className="bg-blue-500/10 border-blue-500/30 text-blue-500 hover:bg-blue-500/20"
            >
              <Eye size={14} className="mr-1" />
              View Roster
            </Button>
            
            {/* Waitlist Button - shows when session is full */}
            {isFull && onViewWaitlist && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onViewWaitlist(session)}
                data-testid={`view-waitlist-${session.session_id}`}
                className="bg-orange-500/10 border-orange-500/30 text-orange-500 hover:bg-orange-500/20"
              >
                <Queue size={14} className="mr-1" />
                Waitlist
              </Button>
            )}
            
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(session)}
              data-testid={`edit-session-${session.session_id}`}
              disabled={isCancelled}
            >
              <PencilSimple size={14} className="mr-1" />
              Edit
            </Button>
            
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.location.href = `/scan?session=${session.session_id}`}
              data-testid={`manage-session-${session.session_id}`}
              disabled={isCancelled}
            >
              Manage Check-ins
            </Button>
            
            <Button
              size="sm"
              variant="outline"
              onClick={() => onExportCSV(session.session_id)}
              data-testid={`export-roster-${session.session_id}`}
            >
              <Download size={14} className="mr-1" />
              CSV
            </Button>
            
            {/* Copy Session Button */}
            {onCopy && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onCopy(session)}
                data-testid={`copy-session-${session.session_id}`}
                title="Copy session to new date"
              >
                <Copy size={14} className="mr-1" />
                Copy
              </Button>
            )}
            
            {/* Message Parents Button */}
            {onMessage && session.registered_count > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onMessage(session)}
                data-testid={`message-session-${session.session_id}`}
                title="Send message to parents"
              >
                <EnvelopeSimple size={14} className="mr-1" />
                Message
              </Button>
            )}
            
            {/* Photos Button */}
            {onViewPhotos && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onViewPhotos(session)}
                data-testid={`photos-session-${session.session_id}`}
                title="Manage session photos"
              >
                <Camera size={14} className="mr-1" />
                Photos
              </Button>
            )}
            
            {/* Cancel or Restore Button */}
            {isCancelled ? (
              <Button
                size="sm"
                variant="outline"
                className="text-green-500 hover:text-green-600 hover:bg-green-500/10 border-green-500/30"
                onClick={() => onRestore(session)}
                data-testid={`restore-session-${session.session_id}`}
              >
                <ArrowCounterClockwise size={14} className="mr-1" />
                Restore
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="text-yellow-500 hover:text-yellow-600 hover:bg-yellow-500/10 border-yellow-500/30"
                onClick={() => onCancel(session)}
                disabled={cancellingSession === session.session_id}
                data-testid={`cancel-session-${session.session_id}`}
              >
                <XCircle size={14} className="mr-1" />
                {cancellingSession === session.session_id ? 'Cancelling...' : 'Cancel'}
              </Button>
            )}
            
            {/* Publish/Unpublish Button */}
            {!isCancelled && !isEnded && (
              isDraft ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-green-500 hover:text-green-600 hover:bg-green-500/10 border-green-500/30"
                  onClick={() => onPublish(session)}
                  data-testid={`publish-session-${session.session_id}`}
                >
                  <CloudArrowUp size={14} className="mr-1" />
                  Publish
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => onUnpublish(session)}
                  data-testid={`unpublish-session-${session.session_id}`}
                >
                  <CloudSlash size={14} className="mr-1" />
                  Unpublish
                </Button>
              )
            )}
            
            {/* End Session Button - for published sessions that are done */}
            {!isCancelled && !isDraft && !isEnded && onEndSession && (
              <Button
                size="sm"
                variant="outline"
                className="text-gray-500 hover:text-gray-600 hover:bg-gray-500/10 border-gray-500/30"
                onClick={() => onEndSession(session)}
                data-testid={`end-session-${session.session_id}`}
              >
                <XCircle size={14} className="mr-1" />
                End Session
              </Button>
            )}
            
            {/* Reopen Session Button - for ended sessions */}
            {isEnded && onReopenSession && (
              <Button
                size="sm"
                variant="outline"
                className="text-green-500 hover:text-green-600 hover:bg-green-500/10 border-green-500/30"
                onClick={() => onReopenSession(session)}
                data-testid={`reopen-session-${session.session_id}`}
              >
                <ArrowCounterClockwise size={14} className="mr-1" />
                Reopen
              </Button>
            )}
            
            <Button
              size="sm"
              variant="ghost"
              className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
              onClick={() => onDelete(session.session_id, isCancelled)}
              data-testid={`delete-session-${session.session_id}`}
            >
              <Trash size={14} />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
